import { useRef, useMemo, useLayoutEffect, useEffect } from "react";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "motion/react";

import {
  clsxMerge,
  getFullEntityName,
  getInitialsFromUsername,
} from "shared/lib/helpers";
import { Avatar, AvatarFallback, AvatarImage } from "shared/ui/shadcn/avatar";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { UserIsOnlineMapI } from "shared/lib/interfaces/chat";

import type { ChatMessageI, ChatContactI } from "../../types";
import { ChatUserOnlineIndicator } from "../shared/online-indicator";
import { getChatContactOnlineStatus } from "../../utils";

interface ChatMessagesPropsI {
  messages: ChatMessageI[] | undefined;
  me: ChatContactI;
  counterparty: ChatContactI;
  onlineUserMap: UserIsOnlineMapI | undefined;
  isFetchingNextPage: boolean;
  scrollIndicatorId?: string;
}

const CONTAINER_VARIANTS = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 },
};

type GroupedChatMessageI =
  | { type: "date"; date: string }
  | { type: "message"; message: ChatMessageI };

export const ChatMessages = ({
  messages,
  me,
  counterparty,
  isFetchingNextPage,
  onlineUserMap,
  scrollIndicatorId,
}: ChatMessagesPropsI) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollHeightBeforeLoadRef = useRef(0);

  // Preserve scroll position when loading older messages
  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (isFetchingNextPage) {
      scrollHeightBeforeLoadRef.current = scrollContainer?.scrollHeight || 0;
    } else if (scrollContainer && scrollHeightBeforeLoadRef.current > 0) {
      const newScrollHeight = scrollContainer.scrollHeight;
      const heightDifference =
        newScrollHeight - scrollHeightBeforeLoadRef.current;

      if (heightDifference > 0) {
        scrollContainer.scrollTop += heightDifference;
      }

      scrollHeightBeforeLoadRef.current = 0;
    }
  }, [isFetchingNextPage]);

  // Handle initial scroll and scroll on new message
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer && messages) {
      setTimeout(() => {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "instant",
        });
      }, TIME_INTERVALS.ONE_HUNDRED_MILLISECONDS);
    }
  }, [messages, me.id]);

  const flattenedItems = useMemo(() => {
    if (!messages) {
      return [];
    }

    const groupedMessages = messages.reduce(
      (acc, message) => {
        const date = dayjs(message.timestamp).format("YYYY-MM-DD");

        if (!acc[date]) {
          acc[date] = [];
        }

        acc[date].push(message);

        return acc;
      },
      {} as Record<string, ChatMessageI[]>
    );

    const items: GroupedChatMessageI[] = [];

    Object.entries(groupedMessages)
      .sort(([dateA], [dateB]) => dayjs(dateA).diff(dayjs(dateB)))
      .forEach(([date, dayMessages]) => {
        items.push({ type: "date", date });
        dayMessages
          .sort((a, b) => dayjs(a.timestamp).diff(dayjs(b.timestamp)))
          .forEach((message) => {
            items.push({ type: "message", message });
          });
      });

    return items;
  }, [messages]);

  if (!messages) {
    return null;
  }

  return (
    <motion.div
      ref={scrollContainerRef}
      className="flex-1 overflow-y-auto p-4"
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
    >
      {scrollIndicatorId && (
        <div id={scrollIndicatorId} className="h-1 w-full" />
      )}

      <AnimatePresence>
        {flattenedItems.map((item) => {
          if (item.type === "date") {
            return (
              <motion.div layout key={item.date} variants={ITEM_VARIANTS}>
                <div className="my-4 flex items-center">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="mx-4 flex-shrink text-xs text-gray-500">
                    {dayjs(item.date).format("MMMM D, YYYY").toUpperCase()}
                  </span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
              </motion.div>
            );
          }

          const { message } = item;
          const isMyMessage = message.sender_id === me.id;
          const sender = isMyMessage ? me : counterparty;
          const senderFullName = getFullEntityName(sender);
          const senderInitials = getInitialsFromUsername(senderFullName);
          const isCounterpartyOnline = isMyMessage
            ? null
            : getChatContactOnlineStatus(onlineUserMap, sender);

          return (
            <motion.div
              key={`${message.sender_id}-${message.timestamp}`}
              layout
              variants={ITEM_VARIANTS}
              className={clsxMerge(
                "mb-4 flex items-end gap-2",
                isMyMessage ? "justify-end" : "justify-start"
              )}
            >
              {!isMyMessage && (
                <div className="relative flex-shrink-0">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={sender.profile_pic_url}
                      alt={senderFullName}
                    />

                    <AvatarFallback>{senderInitials}</AvatarFallback>
                  </Avatar>

                  {isCounterpartyOnline !== null && (
                    <ChatUserOnlineIndicator
                      className="animate-fadein h-2 w-2"
                      isOnline={isCounterpartyOnline}
                    />
                  )}
                </div>
              )}

              <div
                className={clsxMerge(
                  "max-w-md rounded-2xl p-3",
                  isMyMessage
                    ? "rounded-br-none bg-blue-600 text-white"
                    : "rounded-bl-none bg-black text-white"
                )}
              >
                <div className="mb-1 flex items-baseline gap-2">
                  <p className="text-sm font-semibold">{senderFullName}</p>
                  <p className="text-xs text-gray-300">
                    {dayjs(message.timestamp).format("h:mm A")}
                  </p>
                </div>

                {/* Keep the sender's line breaks; break long words inside the bubble */}
                <p className="text-sm whitespace-pre-wrap break-words">
                  {message.message}
                </p>
              </div>

              {isMyMessage && (
                <div className="relative flex-shrink-0">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={sender.profile_pic_url}
                      alt={senderFullName}
                    />

                    <AvatarFallback>{senderInitials}</AvatarFallback>
                  </Avatar>

                  <ChatUserOnlineIndicator className="h-2 w-2" isOnline />
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
};
